import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import translate from '../../commonComponents/translate';
import withCSRF from '../../commonComponents/withCSRF';
import FormInput from '../../commonComponents/forms/FormInput';
import { compose, decodeString } from '../../services/utilityFunctions';

// Only accept same-origin relative paths to avoid open-redirect issues.
const isSafeLocalPath = value =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.startsWith('/') &&
  !value.startsWith('//');

const resolveRedirect = raw => {
  if (!raw) return '/';
  // Plain URL path passed straight through, e.g. ?redirect=/dashboard.
  if (isSafeLocalPath(raw)) return raw;
  // Otherwise treat it as base64-encoded JSON (the same encoding the
  // password signin server-side flow uses via decodeString).
  try {
    const decoded = decodeString(raw);
    if (isSafeLocalPath(decoded)) return decoded;
  } catch (err) {
    // fall through to default
  }
  return '/';
};

const MULTI_ORG_ERROR = 'ERROR_MULTIPLE_ORGANIZATIONS_DETECTED';

/**
 * BRAVON-6245: OTP email sign-in form for the legacy user portal.
 * Three-step flow: email -> code -> org-select (only on multi-org).
 * Talks to /signin/otp/request and /signin/otp/validate (see signin handler).
 */
class OtpForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 'email',
      email: '',
      code: '',
      organizations: [],
      selectedOrg: '',
      error: null,
      loading: false,
    };
    this.handleField = this.handleField.bind(this);
    this.handleSendCode = this.handleSendCode.bind(this);
    this.handleVerify = this.handleVerify.bind(this);
    this.handleOrgSubmit = this.handleOrgSubmit.bind(this);
    this.handleBackToEmail = this.handleBackToEmail.bind(this);
  }

  handleField(name, value) {
    this.setState({ [name]: value });
  }

  async postJSON(path, body) {
    const { _csrf } = this.props;
    const res = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-TOKEN': _csrf,
      },
      body: JSON.stringify({ ...body, _csrf }),
    });
    const json = await res.json().catch(() => ({}));
    return { ok: res.ok && json && json.success !== false, json };
  }

  async handleSendCode(e) {
    if (e && e.preventDefault) e.preventDefault();
    const { email } = this.state;
    const { t } = this.props;
    if (!email) return;
    this.setState({ loading: true, error: null });
    const { ok, json } = await this.postJSON('/signin/otp/request', { email });
    if (!ok) {
      this.setState({
        loading: false,
        error: (json && json.error) || t('login_otp_error_generic'),
      });
      return;
    }
    this.setState({ loading: false, step: 'code', code: '' });
  }

  async handleVerify(e) {
    if (e && e.preventDefault) e.preventDefault();
    const { email, code } = this.state;
    const { t, redirectPath } = this.props;
    if (!code || code.length < 6) return;
    this.setState({ loading: true, error: null });
    const { ok, json } = await this.postJSON('/signin/otp/validate', {
      email,
      code,
    });
    if (!ok) {
      if (json && json.error === MULTI_ORG_ERROR && Array.isArray(json.data)) {
        this.setState({
          loading: false,
          step: 'org-select',
          organizations: json.data,
          selectedOrg: json.data[0] ? json.data[0].identifierCode : '',
        });
        return;
      }
      this.setState({
        loading: false,
        error: (json && json.error) || t('login_otp_error_invalid_code'),
      });
      return;
    }
    window.location.assign(resolveRedirect(redirectPath));
  }

  async handleOrgSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    const { email, code, selectedOrg } = this.state;
    const { t, redirectPath } = this.props;
    if (!selectedOrg) return;
    this.setState({ loading: true, error: null });
    const { ok, json } = await this.postJSON('/signin/otp/validate', {
      email,
      code,
      organizationIdentifier: selectedOrg,
    });
    if (!ok) {
      this.setState({
        loading: false,
        error: (json && json.error) || t('login_otp_error_generic'),
      });
      return;
    }
    window.location.assign(resolveRedirect(redirectPath));
  }

  handleBackToEmail() {
    this.setState({
      step: 'email',
      code: '',
      organizations: [],
      selectedOrg: '',
      error: null,
    });
  }

  render() {
    const {
      t,
      onBackToPassword,
      showBackToPassword,
      dropdownParent,
      variant,
    } = this.props;
    const {
      step,
      email,
      code,
      organizations,
      selectedOrg,
      error,
      loading,
    } = this.state;

    // OtpForm is shared between two contexts with opposite color needs:
    //  - 'page'  : the standalone /signin page (white background) — match the
    //              brand-colored Sign In button / link (SignInForm.jsx).
    //  - 'modal' : the login popup (brand-colored left panel) — match the dark
    //              Login button (LoginForm.jsx) and the inverse white link
    //              (m-button-forgot), so the buttons stay visible on the panel.
    const isModal = variant === 'modal';

    const submitButtonClasses = cx(
      'btn',
      'm-btn',
      'm-btn--pill',
      'm-btn--custom',
      'm-btn--air',
      'm-login__btn',
      isModal && 'login-button',
      !isModal && 'btn-dynamic',
      !isModal && 'm-login__btn--brand',
      { 'm-loader': loading },
      { 'm-loader--right': loading },
      { 'm-loader--light': loading },
    );

    // Secondary link buttons (back-to-password / change-email).
    const secondaryLinkClassName = isModal ? 'm-button-forgot' : 'btn-link m-link';
    const secondaryLinkStyle = isModal
      ? undefined
      : { background: 'transparent', border: 'none', padding: 0 };

    return (
      <div className="m-login__signin">
        <div className="m-login__head">
          <h3 className="m-login__title">{t('login_otp_title')}</h3>
        </div>
        {error && (
          <div className="alert alert-danger" role="alert">
            {t(error) || error}
          </div>
        )}
        {step === 'email' && (
          <form
            className="m-login__form m-form"
            onSubmit={this.handleSendCode}
          >
            <p className="m-login__desc" style={{ color: '#fff' }}>{t('login_otp_email_description')}</p>
            <FormInput
              id="otp-email"
              type="email"
              name="email"
              placeholder={t('email_holder')}
              value={email}
              onChange={value => this.handleField('email', value)}
              inputClassName="login-input"
              inputProps={{ autoComplete: 'email', required: true }}
            />
            <div className="m-login__form-action">
              <button
                type="submit"
                disabled={loading || !email}
                className={submitButtonClasses}
              >
                {t('login_otp_send_code')}
              </button>
            </div>
            {showBackToPassword && (
              <div className="m-login__form-action m--align-center">
                <button
                  type="button"
                  onClick={onBackToPassword}
                  className={secondaryLinkClassName}
                  style={secondaryLinkStyle}
                >
                  {t('login_otp_back_to_password')}
                </button>
              </div>
            )}
          </form>
        )}
        {step === 'code' && (
          <form
            className="m-login__form m-form"
            onSubmit={this.handleVerify}
          >
            <p className="m-login__desc" style={{ color: '#fff' }}>
              {t('login_otp_code_description', email)}
            </p>
            <FormInput
              id="otp-code"
              type="text"
              name="code"
              placeholder="------"
              value={code}
              onChange={value =>
                this.handleField('code', value.replace(/\D/g, ''))
              }
              inputClassName="login-input"
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]{6}',
                maxLength: 6,
                autoComplete: 'one-time-code',
                required: true,
              }}
            />
            <div className="m-login__form-action">
              <button
                type="submit"
                disabled={loading || code.length < 6}
                className={submitButtonClasses}
              >
                {t('login_otp_verify')}
              </button>
            </div>
            <div className="m-login__form-action m--align-center">
              <button
                type="button"
                onClick={this.handleBackToEmail}
                className={secondaryLinkClassName}
                style={secondaryLinkStyle}
              >
                {t('login_otp_change_email')}
              </button>
            </div>
          </form>
        )}
        {step === 'org-select' && (
          <form
            className="m-login__form m-form"
            onSubmit={this.handleOrgSubmit}
          >
            <p className="m-login__desc" style={{ color: '#fff' }}>
              {t('login_otp_select_organization_title')}
            </p>
            {(() => {
              const orgSelect = (
                <FormInput
                  id="otp-org"
                  type="select"
                  value={selectedOrg}
                  onChange={value => this.handleField('selectedOrg', value)}
                  placeholder={t('login_otp_select_organization_title')}
                  // In the modal, match the password-login org selector
                  // (LoginForm.jsx) so it inherits the transparent/white
                  // #login-organization-select styling on the brand panel.
                  inputClassName={
                    isModal ? 'form-control m-input login-input' : 'login-input'
                  }
                  containerClassName={
                    isModal ? 'form-control m-input login-input' : ''
                  }
                  groupProps={
                    isModal
                      ? { className: 'form-control m-input login-input' }
                      : undefined
                  }
                  inputProps={{
                    items: organizations.map(org => ({
                      id: org.identifierCode,
                      text: org.name,
                    })),
                    searchable: true,
                    required: true,
                    // Only scope the dropdown to a parent when rendered inside a
                    // modal (#modalBody). On the standalone /signin page there is
                    // no such element, so leaving it unset lets select2 attach
                    // the dropdown to the body — otherwise it can't be opened.
                    ...(dropdownParent ? { dropdownParent } : {}),
                  }}
                />
              );
              return isModal ? (
                <div id="login-organization-select">{orgSelect}</div>
              ) : (
                orgSelect
              );
            })()}
            <div className="m-login__form-action">
              <button
                type="submit"
                disabled={loading || !selectedOrg}
                className={submitButtonClasses}
              >
                {t('login_signIn')}
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }
}

OtpForm.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  onBackToPassword: PropTypes.func,
  showBackToPassword: PropTypes.bool,
  redirectPath: PropTypes.string,
  /** select2 dropdownParent selector — set only when inside a modal (#modalBody) */
  dropdownParent: PropTypes.string,
  /** Color context: 'page' (white bg, brand buttons) or 'modal' (brand panel, dark/inverse) */
  variant: PropTypes.oneOf(['page', 'modal']),
};

OtpForm.defaultProps = {
  onBackToPassword: () => {},
  showBackToPassword: false,
  redirectPath: '',
  dropdownParent: null,
  variant: 'page',
};

export default compose(withCSRF, translate)(OtpForm);
